import { combineLatest, Observable } from 'rxjs';
import { filter, map, scan } from 'rxjs/operators';

import {
  Amount,
  NO_MEAL_VARIANT,
  NO_UPGR_MEAL_VARIANT,
  SeatCharacteristics,
  SelectionsPerTraveler,
  TravelerService,
} from '@fcom/dapi/interfaces';
import {
  Category,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairItineraryItemType,
  FinnairOrder,
  SeatInfo,
  SeatmapSvgAndControlData,
  SeatPrice,
} from '@fcom/dapi/api/models';
import { isEmpty, PreciseNumber, valuesOf } from '@fcom/core/utils';
import { SeatMapFlightInfo, SeatMapSeatInfo } from '@fcom/common/interfaces';

import { PriceAndFragment, seatCharacteristicsOrder, SeatMapTraveler, seatTypeInfo } from '../interfaces';

export const getDefaultMealVariant = (flight: FinnairItineraryItemFlight): string => {
  if (flight && (!flight.isShortHaul || flight.cabinClass === 'BUSINESS')) {
    return NO_UPGR_MEAL_VARIANT;
  }
  return NO_MEAL_VARIANT;
};

export const getSeatPrice = (seat: SeatInfo, travelerId: string): SeatPrice => {
  return seat?.prices.find((p: SeatPrice) => p.travelerIds.indexOf(travelerId) !== -1);
};

export const getSeatInfoForTracking = (
  seatMap: SeatmapSvgAndControlData,
  seatElement: Element,
  activePax: SeatMapTraveler
): SeatMapSeatInfo => {
  const seatNo = seatElement.getAttribute('data-id');
  const seatInfo = seatMap.seats.find((s) => s.seatNumber === seatNo);
  const price = getSeatPrice(seatInfo, activePax.id);
  return {
    seatNo,
    seatType: getSeatType(seatInfo.characteristics).className,
    price: price?.price?.amount,
    currency: price?.price?.currencyCode,
  };
};

export const getSeatMapFlightInfoFromCart = (
  cartOrOrder: FinnairCart | FinnairOrder
): SeatMapFlightInfo | undefined => {
  if (!cartOrOrder) {
    return undefined;
  }
  const bound = cartOrOrder.bounds[0];
  const itinerary = bound.itinerary.find(
    (i) => i.type === FinnairItineraryItemType.FLIGHT
  ) as FinnairItineraryItemFlight;
  const price = cartOrOrder.prices?.unpaid?.flight?.total.totalAmount;
  return {
    origin: bound.departure.locationCode,
    destination: bound.arrival.locationCode,
    departureTime: bound.departure.dateTime,
    acType: itinerary.aircraft.name,
    flightNo: itinerary.flightNumber,
    ticketType: bound.fareFamily.name,
    price: price?.amount,
    currency: price?.currencyCode,
  };
};

export const getSeatMapFlight = (flight: FinnairItineraryItemFlight): SeatMapFlightInfo | undefined => {
  if (!flight) {
    return undefined;
  }
  return {
    origin: flight.departure.locationCode,
    destination: flight.arrival.locationCode,
    departureTime: flight.departure.dateTime,
    acType: flight.aircraft?.name,
    flightNo: flight.flightNumber,
    ticketType: flight.bookingClass,
  };
};

export const getSeatType = (characteristics: string[] = []): { className: string; label: string; cmsKey: string } => {
  if (characteristics.length === 0) {
    return seatTypeInfo['regular'];
  }

  const orderedCharacteristics = [...characteristics].sort((a: SeatCharacteristics, b: SeatCharacteristics) => {
    const aIndex = seatCharacteristicsOrder.indexOf(a);
    const bIndex = seatCharacteristicsOrder.indexOf(b);

    // Deprioritise characteristics not in ordering array
    const aValue = aIndex === -1 ? seatCharacteristicsOrder.length + 1 : aIndex;
    const bValue = bIndex === -1 ? seatCharacteristicsOrder.length + 1 : bIndex;

    return aValue - bValue;
  });

  return seatTypeInfo[orderedCharacteristics[0]] || seatTypeInfo['regular'];
};

export const sumOfPricesForTravelersServiceSelection = (
  selections: SelectionsPerTraveler<TravelerService | TravelerService[]>
): Amount => {
  const prices: Amount[] = valuesOf(selections)
    .filter(Boolean)
    .reduce<TravelerService[]>((all: TravelerService[], service: TravelerService | TravelerService[]) => {
      const services = Array.isArray(service) ? service : [service];
      return all.concat(
        services.filter(Boolean).flatMap((s) => (s.quantity > 1 ? Array.from({ length: s.quantity }, () => s) : s))
      );
    }, [])
    .map((service) => service.pricePerItem);

  if (isEmpty(prices)) {
    return undefined;
  }

  const currencyCode: string = prices[0].currencyCode;
  const currencyCodesMatch = !prices.find((a: Amount) => a.currencyCode !== currencyCode);
  if (!currencyCodesMatch) {
    // Do not show price if we can't calculate it
    return undefined;
  }

  const amount: string = prices
    .map((p: Amount) => p.amount)
    .reduce((total: PreciseNumber, current: string) => total.add(PreciseNumber.fromString(current)), PreciseNumber.ZERO)
    .toString();
  return { amount, currencyCode };
};

export const calculateSubTotalPrice = (
  subtotalPriceSub$: Observable<Amount & { category: Category; fragmentId: string }>,
  categorySubject$: Observable<Category>
): Observable<Amount> => {
  const categoryPrices$ = subtotalPriceSub$.pipe(
    scan((acc: { [key: string]: PriceAndFragment[] }, value: Amount & { category: Category; fragmentId: string }) => {
      const newValue: PriceAndFragment = {
        price: { currencyCode: value.currencyCode, amount: value.amount },
        fragmentId: value.fragmentId,
      };
      if (!acc[value.category.toString()]) {
        acc[value.category.toString()] = [newValue];
      } else {
        const oldFragment = acc[value.category.toString()].find((v) => v.fragmentId === newValue.fragmentId);
        if (oldFragment) {
          oldFragment.price = newValue.price;
        } else {
          acc[value.category.toString()].push(newValue);
        }
      }
      return acc;
    }, {})
  );

  return combineLatest([categoryPrices$, categorySubject$.pipe(filter(Boolean))]).pipe(
    map(([categoryPrices, category]) => {
      const prices = categoryPrices[category.toString()];

      if (Array.isArray(prices) && prices?.length) {
        return prices
          .filter((a) => !!a.price.amount)
          .reduce(
            (all: Amount, priceAndFragment: PriceAndFragment) => {
              if (!all.currencyCode) {
                all.currencyCode = priceAndFragment.price.currencyCode;
              }
              all.amount = String(+priceAndFragment.price.amount + +all.amount);
              return all;
            },
            { amount: '0', currencyCode: null }
          );
      } else {
        return { amount: '0', currencyCode: null };
      }
    })
  );
};
